import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import { getUserById, createUserSchedules } from '../../services/users/usersService';
import { ScheduleActionDto, UserDto, ReminderDto } from '../../services/users/types';

const ScheduleCreate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [scheduleType, setScheduleType] = useState<'ONCE' | 'RECURRING'>('ONCE');
  const [repetitionType, setRepetitionType] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'>('DAILY');
  const [recurringUntil, setRecurringUntil] = useState('');
  const [timezone, setTimezone] = useState<'AMERICA_SAO_PAULO' | 'UTC'>('AMERICA_SAO_PAULO');
  
  // Reminders
  const [reminders, setReminders] = useState<ReminderDto[]>([]);
  const [reminderInterval, setReminderInterval] = useState<'MINUTES' | 'HOURS' | 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS'>('HOURS');
  const [reminderValue, setReminderValue] = useState(1);
  const [reminderName, setReminderName] = useState('');
  const [reminderDescription, setReminderDescription] = useState('');
  const [reminderTypes, setReminderTypes] = useState<Array<'BOT' | 'EMAIL' | 'SMS'>>(['BOT']);
  
  // Form validation
  const [errors, setErrors] = useState<{
    name?: string;
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    recurringUntil?: string;
    reminderValue?: string;
    reminderName?: string;
  }>({});
  
  // Load user data
  useEffect(() => {
    if (id) {
      fetchUserData(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  const fetchUserData = async (userId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userData = await getUserById(auth.user || null, userId);
      setUser(userData);
      
      // Set default times (current time + 1 hour for end)
      const now = new Date();
      const nowPlus1Hour = new Date(now.getTime() + 60 * 60 * 1000);
      
      setStartDate(formatDateForInput(now));
      setStartTime(formatTimeForInput(now));
      setEndDate(formatDateForInput(nowPlus1Hour));
      setEndTime(formatTimeForInput(nowPlus1Hour));
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load user data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Helper to format date for input field
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };
  
  // Helper to format time for input field
  const formatTimeForInput = (date: Date): string => {
    return date.toTimeString().slice(0, 5);
  };
  
  // Helper to format date for backend (compatible with Java's LocalDateTime)
  const formatDateForBackend = (date: Date): string => {
    // Format as YYYY-MM-DDThh:mm:ss.000 without the timezone Z
    const isoString = date.toISOString();
    return isoString.slice(0, 19) + '.000';
  };
  
  // Add a reminder
  const handleAddReminder = () => {
    if (!reminderName.trim()) {
      setErrors(prev => ({ ...prev, reminderName: 'Reminder name is required' }));
      return;
    }
    
    if (reminderValue <= 0) {
      setErrors(prev => ({ ...prev, reminderValue: 'Value must be greater than zero' }));
      return;
    }
    
    const newReminder: ReminderDto = {
      interval: reminderInterval,
      value: reminderValue,
      active: true,
      name: reminderName,
      description: reminderDescription,
      reminder_types: [...reminderTypes]
    };
    
    setReminders(prev => [...prev, newReminder]);
    
    // Reset reminder form
    setReminderInterval('HOURS');
    setReminderValue(1);
    setReminderName('');
    setReminderDescription('');
    setReminderTypes(['BOT']);
    setErrors(prev => ({ ...prev, reminderName: undefined, reminderValue: undefined }));
  };
  
  // Remove a reminder
  const handleRemoveReminder = (index: number) => {
    setReminders(prev => prev.filter((_, i) => i !== index));
  };
  
  // Toggle reminder type
  const handleToggleReminderType = (type: 'BOT' | 'EMAIL' | 'SMS') => {
    setReminderTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!startTime) {
      newErrors.startTime = 'Start time is required';
    }
    
    if (!endDate) {
      newErrors.endDate = 'End date is required';
    }
    
    if (!endTime) {
      newErrors.endTime = 'End time is required';
    }
    
    // Check if end date/time is after start date/time
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    
    if (endDateTime <= startDateTime) {
      newErrors.endDate = 'End date/time must be after start date/time';
    }
    
    // Check recurring until date for recurring schedules
    if (scheduleType === 'RECURRING' && recurringUntil) {
      const recurringUntilDate = new Date(`${recurringUntil}T23:59:59`);
      if (recurringUntilDate <= startDateTime) {
        newErrors.recurringUntil = 'Recurring end date must be after start date';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Create the schedule object
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const endDateTime = new Date(`${endDate}T${endTime}`);
      
      const scheduleData: ScheduleActionDto = {
        timezone,
        parameters: {
          name,
          description
        },
        reminders: reminders.length > 0 ? [...reminders] : undefined,
        action_type: "CUSTOM_TASK",
        scheduled_type: scheduleType,
        starts_in: formatDateForBackend(startDateTime),
        ends_in: formatDateForBackend(endDateTime),
        repetition_type: scheduleType === 'RECURRING' ? repetitionType : undefined,
        recurring_until: scheduleType === 'RECURRING' && recurringUntil 
          ? formatDateForBackend(new Date(`${recurringUntil}T23:59:59`)) 
          : undefined
      };
      
      if (id) {
        await createUserSchedules(auth.user || null, id, [scheduleData]);
        navigate(`/users/${id}/schedules`);
      }
    } catch (err) {
      console.error('Error creating schedule:', err);
      setError('Failed to create schedule. Please check the data and try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancel = () => {
    navigate(`/users/${id}/schedules`);
  };
  
  return (
    <div>
      <PageHeader
        title={isLoading ? 'Loading...' : 'Create Schedule'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Users", to: "/users" },
          { label: user?.first_name && user?.last_name 
            ? `${user.first_name} ${user.last_name}` 
            : user?.username || 'User', to: `/users/${id}` },
          { label: "Schedules", to: `/users/${id}/schedules` },
          { label: isLoading ? 'Loading...' : 'Create Schedule' }
        ]}
      />
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={() => id && fetchUserData(id)}
            >
              Retry
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <FormInput
                    label="Schedule Name"
                    placeholder="e.g., Weekly Meeting"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    error={errors.name}
                  />
                  
                  <FormInput
                    label="Description"
                    placeholder="Optional schedule description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Schedule Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio text-blue-600"
                          checked={scheduleType === 'ONCE'}
                          onChange={() => setScheduleType('ONCE')}
                        />
                        <span className="ml-2">Once</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio text-blue-600"
                          checked={scheduleType === 'RECURRING'}
                          onChange={() => setScheduleType('RECURRING')}
                        />
                        <span className="ml-2">Recurring</span>
                      </label>
                    </div>
                  </div>
            
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      required
                      error={errors.startDate}
                    />
                    <FormInput
                      label="Start Time"
                      type="time"
                      value={startTime}
                      onChange={e => setStartTime(e.target.value)}
                      required
                      error={errors.startTime}
                    />
                    <FormInput
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      required
                      error={errors.endDate}
                    />
                    <FormInput
                      label="End Time"
                      type="time"
                      value={endTime}
                      onChange={e => setEndTime(e.target.value)}
                      required
                      error={errors.endTime}
                    />
                  </div>
            
                  {scheduleType === 'RECURRING' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Repetition Pattern</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <label className="inline-flex items-center border rounded-md p-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              className="form-radio text-blue-600"
                              checked={repetitionType === 'DAILY'}
                              onChange={() => setRepetitionType('DAILY')}
                            />
                            <span className="ml-2">Daily</span>
                          </label>
                          <label className="inline-flex items-center border rounded-md p-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              className="form-radio text-blue-600"
                              checked={repetitionType === 'WEEKLY'}
                              onChange={() => setRepetitionType('WEEKLY')}
                            />
                            <span className="ml-2">Weekly</span>
                          </label>
                          <label className="inline-flex items-center border rounded-md p-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              className="form-radio text-blue-600"
                              checked={repetitionType === 'MONTHLY'}
                              onChange={() => setRepetitionType('MONTHLY')}
                            />
                            <span className="ml-2">Monthly</span>
                          </label>
                          <label className="inline-flex items-center border rounded-md p-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              className="form-radio text-blue-600"
                              checked={repetitionType === 'YEARLY'}
                              onChange={() => setRepetitionType('YEARLY')}
                            />
                            <span className="ml-2">Yearly</span>
                          </label>
                        </div>
                      </div>
                      
                      <FormInput
                        label="Recur Until (optional)"
                        type="date"
                        value={recurringUntil}
                        onChange={e => setRecurringUntil(e.target.value)}
                        helperText="Leave blank to recur indefinitely"
                        error={errors.recurringUntil}
                      />
                    </>
                  )}
            
                  <FormSelect
                    label="Timezone"
                    value={timezone}
                    onChange={e => setTimezone(e.target.value as 'AMERICA_SAO_PAULO' | 'UTC')}
                    options={[
                      { value: "AMERICA_SAO_PAULO", label: "America/Sao Paulo" },
                      { value: "UTC", label: "UTC" }
                    ]}
                  />
                </div>
              </div>
          
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reminders</h3>
            
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Reminder Name"
                      placeholder="e.g., Meeting Reminder"
                      value={reminderName}
                      onChange={e => setReminderName(e.target.value)}
                      error={errors.reminderName}
                    />
                    
                    <FormInput
                      label="Reminder Description (optional)"
                      placeholder="Reminder description"
                      value={reminderDescription}
                      onChange={e => setReminderDescription(e.target.value)}
                    />
                  </div>
            
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Remind Before</label>
                      <div className="flex">
                        <input
                          type="number"
                          min="1"
                          className={`block w-full px-3 py-2 border rounded-l-md focus:outline-none focus:ring-2 ${
                            errors.reminderValue
                              ? 'border-red-300 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-blue-500'
                          }`}
                          value={reminderValue}
                          onChange={e => setReminderValue(parseInt(e.target.value) || 0)}
                        />
                        <select
                          className="block px-3 py-2 border border-gray-300 rounded-r-md border-l-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={reminderInterval}
                          onChange={e => setReminderInterval(e.target.value as any)}
                        >
                          <option value="MINUTES">Minutes</option>
                          <option value="HOURS">Hours</option>
                          <option value="DAYS">Days</option>
                          <option value="WEEKS">Weeks</option>
                          <option value="MONTHS">Months</option>
                        </select>
                      </div>
                      {errors.reminderValue && (
                        <p className="mt-1 text-sm text-red-600">{errors.reminderValue}</p>
                      )}
                    </div>
              
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notification Type</label>
                      <div className="space-y-2">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={reminderTypes.includes('BOT')}
                            onChange={() => handleToggleReminderType('BOT')}
                          />
                          <span className="ml-2">Bot</span>
                        </label>
                        <label className="inline-flex items-center ml-4">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={reminderTypes.includes('EMAIL')}
                            onChange={() => handleToggleReminderType('EMAIL')}
                          />
                          <span className="ml-2">Email</span>
                        </label>
                        <label className="inline-flex items-center ml-4">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={reminderTypes.includes('SMS')}
                            onChange={() => handleToggleReminderType('SMS')}
                          />
                          <span className="ml-2">SMS</span>
                        </label>
                      </div>
                    </div>
              
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleAddReminder}
                        className="w-full"
                      >
                        Add Reminder
                      </Button>
                    </div>
                  </div>
            
                  {reminders.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Added Reminders</h4>
                      <div className="space-y-2">
                        {reminders.map((reminder, index) => (
                          <div key={index} className="flex justify-between items-center p-3 border border-gray-200 rounded-md">
                            <div>
                              <span className="font-medium">{reminder.name}</span>
                              <span className="text-sm text-gray-500 ml-2">
                                {reminder.value} {reminder.interval.toLowerCase()} before
                              </span>
                              <div className="text-xs mt-1 flex flex-wrap gap-1">
                                {reminder.reminder_types.map((type, idx) => (
                                  <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    type === 'BOT' ? 'bg-blue-100 text-blue-700' :
                                    type === 'EMAIL' ? 'bg-purple-100 text-purple-700' :
                                    type === 'SMS' ? 'bg-orange-100 text-orange-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              onClick={() => handleRemoveReminder(index)}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Create Schedule'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ScheduleCreate;